import React from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import Avatar from './Avatar.jsx';
import { useAppMotion } from '../../hooks/useAppMotion';

/**
 * Ueberlappender Avatar-Stapel (Okt 2026, „App wie Spot“).
 *
 * Im Werbespot sitzt er unten auf jeder Session-Karte (Motion-Kit `.eo-people`):
 * 32px-Koepfe, 9px Ueberlappung, getrennt durch den hellen Schnitt des
 * Avatar-Rahmens. Bis dahin hatte die App keinen Stapel — nur die archivierte
 * Otterconomy baute einen eigenen mit hart weissem Ring (im Dunkeln falsch).
 *
 * - people:     [{ id, name, src }] in Anzeigereihenfolge (Host zuerst)
 * - size:       '2xs' (20, Meta-Zeilen) | 'xs' (24) | 'sm' (32, Karten) | 'md' (40)
 * - max:        so viele Koepfe, der Rest wird „+n“
 * - total:      Gesamtzahl, falls `people` schon gekuerzt ist
 * - animateNew: wer neu dazukommt, ploppt herein (der erste Render nicht)
 * - decorative: nur Hinweis, z.B. in einer Zeile, die selbst beschriftet ist
 */

const OVERLAP = {
  '2xs': '-ml-1.5',
  xs: '-ml-2',
  sm: '-ml-[9px]',
  md: '-ml-3',
};

const PLUS = {
  '2xs': 'w-5 h-5 text-[9px]',
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-8 h-8 text-2xs',
  md: 'w-10 h-10 text-xs',
};

export const describePeople = (names, total) => {
  const shown = names.filter(Boolean);
  const rest = Math.max(0, total - shown.length);
  if (shown.length === 0) return rest ? `${rest} Personen` : '';
  if (rest === 0) {
    if (shown.length === 1) return shown[0];
    return `${shown.slice(0, -1).join(', ')} und ${shown[shown.length - 1]}`;
  }
  return `${shown.join(', ')} und ${rest} weitere`;
};

const AvatarStack = ({
  people = [],
  size = 'sm',
  max = 4,
  total,
  animateNew = true,
  decorative = false,
  label,
  className = '',
}) => {
  const { popIn } = useAppMotion();
  const visible = people.slice(0, max);
  const count = Math.max(total ?? people.length, people.length);
  const rest = count - visible.length;
  if (count === 0) return null;

  const a11y = decorative
    ? { 'aria-hidden': true }
    : { role: 'img', 'aria-label': label || describePeople(visible.map((p) => p.name), count) };

  return (
    <div className={`flex items-center ${className}`} {...a11y}>
      <AnimatePresence initial={false}>
        {visible.map((person, index) => (
          <motion.span
            key={person.id ?? `${person.name}-${index}`}
            className={`relative inline-flex ${index === 0 ? '' : OVERLAP[size] || OVERLAP.sm}`}
            // Der vordere Kopf liegt oben, wie bei Karten auf einem Tisch.
            style={{ zIndex: visible.length - index }}
            {...(animateNew ? popIn : {})}
          >
            <Avatar name={person.name} src={person.src} size={size} aria-hidden="true" />
          </motion.span>
        ))}
      </AnimatePresence>
      {rest > 0 && (
        <span
          className={[
            'relative inline-flex items-center justify-center rounded-full avatar-frame',
            'bg-chip text-muted-foreground font-semibold tabular-nums leading-none',
            OVERLAP[size] || OVERLAP.sm,
            PLUS[size] || PLUS.sm,
          ].join(' ')}
          aria-hidden="true"
        >
          +{rest}
        </span>
      )}
    </div>
  );
};

AvatarStack.propTypes = {
  people: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      src: PropTypes.string,
    })
  ),
  size: PropTypes.oneOf(['2xs', 'xs', 'sm', 'md']),
  max: PropTypes.number,
  total: PropTypes.number,
  animateNew: PropTypes.bool,
  decorative: PropTypes.bool,
  label: PropTypes.string,
  className: PropTypes.string,
};

export default AvatarStack;
